/**
 * Sticky Add-to-Cart Bar (Mobile Only)
 * Syncs with main product form for variants, quantity, and price
 */

class StickyAtcBar {
  constructor() {
    this.stickyBar = document.querySelector('[data-sticky-bar]');
    if (!this.stickyBar) return;

    this.mediaQuery = window.matchMedia('(max-width: 749px)');
    if (!this.mediaQuery.matches) return;

    this.init();
  }

  init() {
    this.sectionId = this.stickyBar.dataset.sectionId;
    this.mainProductComponent = document.querySelector('[data-main-product-form]');
    this.mainForm = this.mainProductComponent?.querySelector('form');
    this.productData = this.getProductData();

    if (!this.productData) {
      console.error('No product data found for sticky bar');
      return;
    }

    this.priceElement = this.stickyBar.querySelector('[data-sticky-price]');
    this.variantSelect = this.stickyBar.querySelector('[data-sticky-variant-select]');
    this.quantityInput = this.stickyBar.querySelector('[data-sticky-qty-input]');
    this.limitMessage = this.stickyBar.querySelector('[data-sticky-limit-message]');
    this.submitButton = this.stickyBar.querySelector('[data-sticky-button]');
    this.buttonText = this.submitButton?.querySelector('[data-sticky-button-text]');
    this.variantLabel = this.stickyBar.querySelector('[data-sticky-selected-variant]');
    this.defaultButtonLabel = this.buttonText?.textContent?.trim() || '';
    this.maxedOutLabel = this.submitButton?.dataset.maxedOut || '';
    this.minusBtn = this.stickyBar.querySelector('[data-qty-minus]');
    this.plusBtn = this.stickyBar.querySelector('[data-qty-plus]');

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.refreshPrimaryQuantityInput();

    this.currentVariant = this.getCurrentVariant();
    this.updateVariantLabel();
    this.updateQuantityLimits();
    this.syncQuantityToMainForm();
    this.hideLimitMessage();

    this.setupEventListeners();
    this.updateBodyPadding();
    this.subscribeToVariantChanges();
    this.subscribeToCartUpdates();
  }

  getCurrentVariant() {
    const currentId = this.variantSelect ? parseInt(this.variantSelect.value, 10) : this.productData.currentVariantId;
    return this.productData.variants.find((variant) => variant.id === currentId);
  }

  getProductData() {
    const scriptTag = document.querySelector(`[data-sticky-product-data][data-section-id="${this.sectionId}"]`);
    if (scriptTag) {
      try {
        return JSON.parse(scriptTag.textContent);
      } catch (e) {
        console.error('Failed to parse product data:', e);
      }
    }
    return null;
  }

  setupEventListeners() {
    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', (event) => {
        const variantId = parseInt(event.target.value, 10);
        if (!variantId) return;

        const variant = this.productData.variants.find((item) => item.id === variantId);
        if (variant) {
          this.currentVariant = variant;
          this.updateVariant(variantId);
          this.syncVariantToMainForm(variantId);
          this.hideLimitMessage();
        }
      });
    }

    if (this.minusBtn) {
      this.minusBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.changeQuantity(-1);
      });
    }

    if (this.plusBtn) {
      this.plusBtn.addEventListener('click', (event) => {
        event.preventDefault();
        this.changeQuantity(1);
      });
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('change', (event) => {
        const rawValue = parseInt(event.target.value, 10);
        const attempted = Number.isFinite(rawValue) ? rawValue : this.getMinQuantity();
        const normalized = this.normalizeQuantity(attempted);

        this.quantityInput.value = normalized;
        this.quantityInput.setAttribute('value', normalized);
        this.syncQuantityToMainForm();
        this.updateQuantityLimits();

        const maxQty = this.getMaxQuantity();
        if (maxQty <= 0 || attempted > normalized) {
          this.showLimitMessage(maxQty);
        } else {
          this.hideLimitMessage();
        }
      });

      this.quantityInput.addEventListener('input', (event) => {
        const rawValue = parseInt(event.target.value, 10);
        const attempted = Number.isFinite(rawValue) ? rawValue : this.getMinQuantity();
        const normalized = this.normalizeQuantity(attempted);

        this.quantityInput.value = normalized;
        this.quantityInput.setAttribute('value', normalized);
        this.syncQuantityToMainForm();
        this.updateQuantityLimits();

        const maxQty = this.getMaxQuantity();
        if (maxQty <= 0 || attempted > normalized) {
          this.showLimitMessage(maxQty);
        } else {
          this.hideLimitMessage();
        }
      });
    }

    if (this.submitButton) {
      this.submitButton.addEventListener('click', (event) => {
        if (!this.validateQuantityBeforeSubmit()) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        this.syncQuantityToMainForm();
      });

      const targetForm = document.getElementById(this.productData.productFormId);
      if (targetForm) {
        targetForm.addEventListener('submit', (event) => {
          if (!this.validateQuantityBeforeSubmit()) {
            event.preventDefault();
            if (typeof event.stopImmediatePropagation === 'function') {
              event.stopImmediatePropagation();
            }
            return;
          }

          this.syncQuantityToMainForm();
        });
      }
    }

    window.addEventListener('resize', () => {
      if (this.mediaQuery.matches) {
        this.updateBodyPadding();
      } else {
        document.body.classList.remove('has-sticky-atc-bar');
        document.body.style.removeProperty('--sticky-atc-height');
      }
    });
  }

  changeQuantity(delta) {
    if (!this.quantityInput) return;

    const step = this.getQuantityStep();
    const currentRaw = parseInt(this.quantityInput.value, 10);
    const currentValue = Number.isFinite(currentRaw) ? currentRaw : this.getMinQuantity();
    const attemptedValue = currentValue + delta * step;
    const normalizedValue = this.normalizeQuantity(attemptedValue);

    this.quantityInput.value = normalizedValue;
    this.quantityInput.setAttribute('value', normalizedValue);
    this.syncQuantityToMainForm();
    this.updateQuantityLimits();

    const maxQty = this.getMaxQuantity();
    if (maxQty <= 0 || normalizedValue !== attemptedValue) {
      this.showLimitMessage(maxQty);
    } else {
      this.hideLimitMessage();
    }
  }

  getQuantityRules() {
    const rules = this.currentVariant?.quantity_rule || {};
    const min = Number(rules.min);
    const max = Number(rules.max);
    const increment = Number(rules.increment);

    return {
      min: Number.isFinite(min) && min > 0 ? min : 1,
      max: Number.isFinite(max) && max > 0 ? max : null,
      increment: Number.isFinite(increment) && increment > 0 ? increment : 1,
    };
  }

  getPrimaryQuantityInput() {
    if (!this.mainForm) return null;
    return this.mainForm.querySelector('.quantity__input') || this.mainForm.querySelector('input[name="quantity"]');
  }

  refreshPrimaryQuantityInput() {
    this.mainQuantityInput = this.getPrimaryQuantityInput();
  }

  getCurrentCartQuantity() {
    if (!this.mainQuantityInput) return 0;
    const cartQty = Number(this.mainQuantityInput.dataset.cartQuantity);
    return Number.isFinite(cartQty) && cartQty > 0 ? cartQty : 0;
  }

  getQuantityRuleMax() {
    const rules = this.getQuantityRules();
    const datasetMax = Number(this.mainQuantityInput?.dataset.max);
    let max = rules.max;

    if (Number.isFinite(datasetMax) && datasetMax > 0) {
      max = max === null ? datasetMax : Math.min(max, datasetMax);
    }

    return max;
  }

  getMinQuantity() {
    const rules = this.getQuantityRules();
    const datasetMin = Number(this.mainQuantityInput?.dataset.min);
    const minFromDataset = Number.isFinite(datasetMin) && datasetMin > 0 ? datasetMin : null;
    return minFromDataset ? Math.max(rules.min, minFromDataset) : rules.min;
  }

  getQuantityStep() {
    const rules = this.getQuantityRules();
    const datasetStep = Number(this.mainQuantityInput?.step);
    return Number.isFinite(datasetStep) && datasetStep > 0 ? datasetStep : rules.increment;
  }

  normalizeQuantity(value) {
    const minQty = this.getMinQuantity();
    const maxQty = this.getMaxQuantity();
    const step = this.getQuantityStep();

    if (maxQty <= 0) {
      return 0;
    }

    const numericValue = Number.isFinite(value) ? value : minQty;
    let adjusted = numericValue;

    if (step > 1) {
      const offset = adjusted - minQty;
      const steps = Math.floor(offset / step);
      adjusted = minQty + steps * step;
    }

    adjusted = Math.max(minQty, adjusted);
    adjusted = Math.min(adjusted, maxQty);

    return adjusted;
  }

  getMaxQuantity() {
    if (!this.currentVariant) return 0;

    const allowsBackorder = this.currentVariant.inventory_policy === 'continue';
    const inventoryQty = Number(this.currentVariant.inventory_quantity);
    const cartQty = this.getCurrentCartQuantity();

    let maxByInventory = Infinity;
    if (!allowsBackorder && Number.isFinite(inventoryQty)) {
      maxByInventory = inventoryQty - cartQty;
    }

    let maxByRule = Infinity;
    const ruleMax = this.getQuantityRuleMax();
    if (ruleMax !== null && ruleMax !== undefined) {
      maxByRule = ruleMax - cartQty;
    }

    let maxQty = Math.min(maxByInventory, maxByRule);

    if (!Number.isFinite(maxQty)) {
      maxQty = 99;
    }

    if (maxQty < 0) {
      maxQty = 0;
    }

    const minQty = this.getMinQuantity();
    const step = this.getQuantityStep();

    if (Number.isFinite(maxQty) && maxQty > 0 && step > 1) {
      const offset = maxQty - minQty;
      if (offset >= 0) {
        const steps = Math.floor(offset / step);
        maxQty = minQty + steps * step;
      }
    }

    if (maxQty > 0 && maxQty < minQty) {
      maxQty = minQty;
    }

    return maxQty;
  }

  updateQuantityLimits() {
    if (!this.quantityInput) return;

    const maxQty = this.getMaxQuantity();
    const minQty = this.getMinQuantity();
    const step = this.getQuantityStep();

    this.quantityInput.setAttribute('min', minQty);
    if (Number.isFinite(step) && step > 0) {
      this.quantityInput.setAttribute('step', step);
    } else {
      this.quantityInput.removeAttribute('step');
    }

    if (Number.isFinite(maxQty) && maxQty > 0) {
      this.quantityInput.setAttribute('max', maxQty);
    } else {
      this.quantityInput.removeAttribute('max');
    }

    const currentRaw = parseInt(this.quantityInput.value, 10);
    const currentValue = Number.isFinite(currentRaw) ? currentRaw : minQty;
    const normalizedValue = this.normalizeQuantity(currentValue);

    if (normalizedValue !== currentValue) {
      this.quantityInput.value = normalizedValue;
      this.quantityInput.setAttribute('value', normalizedValue);
      this.syncQuantityToMainForm();
    }

    if (this.minusBtn) {
      this.minusBtn.disabled = normalizedValue <= minQty || maxQty <= 0;
    }

    if (this.plusBtn) {
      this.plusBtn.disabled = maxQty <= 0 || normalizedValue >= maxQty;
    }

    if (this.submitButton && this.buttonText) {
      if (!this.currentVariant?.available) {
        this.submitButton.disabled = true;
        this.buttonText.textContent = this.submitButton.dataset.soldOut || 'Agotado';
      } else if (maxQty <= 0) {
        this.submitButton.disabled = true;
        const label = this.maxedOutLabel || 'Maximo alcanzado';
        this.buttonText.textContent = label;
        this.showLimitMessage(0);
      } else {
        this.submitButton.disabled = false;
        this.buttonText.textContent = this.submitButton.dataset.addToCart || this.defaultButtonLabel || 'Agregar al carrito';
      }
    }

    if (maxQty > 0 && normalizedValue < maxQty) {
      this.hideLimitMessage();
    }
  }

  updateVariant(variantId) {
    const variant = this.productData.variants.find((item) => item.id === variantId);
    if (!variant) return;

    this.currentVariant = variant;
    this.updateVariantLabel();
    this.refreshPrimaryQuantityInput();

    if (this.priceElement) {
      this.priceElement.textContent = this.formatMoney(variant.price);
    }

    if (this.submitButton && this.buttonText) {
      if (variant.available) {
        this.submitButton.disabled = false;
        this.buttonText.textContent = this.submitButton.dataset.addToCart || this.defaultButtonLabel || 'Agregar al carrito';
      } else {
        this.submitButton.disabled = true;
        this.buttonText.textContent = this.submitButton.dataset.soldOut || 'Agotado';
      }
    }

    this.updateQuantityLimits();
    this.hideLimitMessage();
  }

  formatMoney(cents) {
    const amount = (cents / 100).toFixed(0);
    return '$' + amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  formatNumber(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      return '0';
    }

    try {
      return new Intl.NumberFormat('es-CL').format(numeric);
    } catch (error) {
      return numeric.toString();
    }
  }

  showLimitMessage(maxQty) {
    if (!this.limitMessage) return;

    const numericQty = Number(maxQty);
    const safeQty = Number.isFinite(numericQty) ? Math.max(0, numericQty) : 0;

    const formattedQty = this.formatNumber(safeQty);

    if (safeQty > 0) {
      this.limitMessage.textContent = `Debido a la disponibilidad, solo se añadió ${formattedQty} artículos al carrito.`;
    } else {
      this.limitMessage.textContent = 'Debido a la disponibilidad, no se añadieron más artículos al carrito.';
    }

    this.limitMessage.hidden = false;
    this.limitMessage.classList.add('is-visible');
  }

  hideLimitMessage() {
    if (!this.limitMessage) return;
    this.limitMessage.hidden = true;
    this.limitMessage.classList.remove('is-visible');
    this.limitMessage.textContent = '';
  }

  syncVariantToMainForm(variantId) {
    if (!this.mainForm) return;

    const variantInput = this.mainForm.querySelector('[name="id"]');
    if (variantInput) {
      variantInput.value = variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const radioInput = this.mainForm.querySelector(`input[type="radio"][value="${variantId}"]`);
    if (radioInput) {
      radioInput.checked = true;
      radioInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const variantPicker =
      this.mainProductComponent?.querySelector('variant-selects, variant-radios') ||
      this.mainForm.querySelector('variant-selects, variant-radios');
    if (variantPicker && typeof variantPicker.updateOptions === 'function') {
      variantPicker.updateOptions();
    }
  }

  syncQuantityToMainForm() {
    if (!this.mainForm || !this.quantityInput) return;

    const rawQty = parseInt(this.quantityInput.value, 10);
    const stickyQty = Number.isFinite(rawQty) ? rawQty : this.getMinQuantity();

    const mainQuantityInputs = this.mainForm.querySelectorAll('input[name="quantity"]');
    mainQuantityInputs.forEach((input) => {
      input.value = stickyQty;
      input.setAttribute('value', stickyQty);
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const quantityComponent = this.mainForm.querySelector('quantity-input');
    if (quantityComponent) {
      const componentInput = quantityComponent.querySelector('input[name="quantity"]');
      if (componentInput) {
        componentInput.value = stickyQty;
        componentInput.setAttribute('value', stickyQty);
        componentInput.dispatchEvent(new Event('change', { bubbles: true }));
        componentInput.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    if (this.productData?.productFormId) {
      const associatedInputs = document.querySelectorAll(
        `input[name="quantity"][form="${this.productData.productFormId}"]`
      );

      associatedInputs.forEach((input) => {
        if (input === this.quantityInput) return;
        input.value = stickyQty;
        input.setAttribute('value', stickyQty);
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    }
  }

  validateQuantityBeforeSubmit() {
    if (!this.quantityInput) return true;

    const maxQty = this.getMaxQuantity();
    if (maxQty <= 0) {
      this.showLimitMessage(maxQty);
      return false;
    }

    const rawValue = parseInt(this.quantityInput.value, 10);
    const currentValue = Number.isFinite(rawValue) ? rawValue : this.getMinQuantity();
    const normalizedValue = this.normalizeQuantity(currentValue);

    if (normalizedValue !== currentValue) {
      this.quantityInput.value = normalizedValue;
      this.quantityInput.setAttribute('value', normalizedValue);
      this.syncQuantityToMainForm();
      if (normalizedValue < currentValue) {
        this.showLimitMessage(maxQty);
      } else {
        this.hideLimitMessage();
      }
    } else {
      this.hideLimitMessage();
    }

    return true;
  }

  subscribeToVariantChanges() {
    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant) {
        const variant = event.detail.variant;

        if (this.variantSelect && variant.id) {
          this.variantSelect.value = variant.id;
        }

        this.updateVariant(variant.id);
      }
    });

    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      document.addEventListener(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.detail && event.detail.data && event.detail.data.variant) {
          const variant = event.detail.data.variant;

          if (this.variantSelect && variant.id) {
            this.variantSelect.value = variant.id;
          }

          this.updateVariant(variant.id);
        }
      });
    }
  }

  subscribeToCartUpdates() {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      document.addEventListener(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      this.cartUpdateEventName = PUB_SUB_EVENTS.cartUpdate;
    } else {
      document.addEventListener('cart:updated', this.handleCartUpdate);
      this.cartUpdateEventName = 'cart:updated';
    }
  }

  handleCartUpdate() {
    this.refreshPrimaryQuantityInput();
    this.updateQuantityLimits();
  }

  updateBodyPadding() {
    if (!this.stickyBar) return;

    const height = this.stickyBar.offsetHeight;
    document.body.classList.add('has-sticky-atc-bar');
    document.body.style.setProperty('--sticky-atc-height', `${height}px`);
  }

  updateVariantLabel() {
    if (!this.variantLabel || !this.currentVariant) return;
    this.variantLabel.textContent = this.currentVariant.title;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyAtcBar();
  });
} else {
  new StickyAtcBar();
}
